import { useQuery } from "@tanstack/react-query";
import { Layout, Spin } from "antd";
import { isAxiosError } from "axios";
import { Outlet, useLocation } from "react-router-dom";
import { useObjects } from "@entities/object";
import { projectQueries } from "@entities/project";
import { EntityBaseAbilityBuilder, useUpdateAbility } from "@shared/lib/casl";
import { useCoordinator } from "@shared/lib/hooks";
import { Navigate, routes } from "@shared/lib/react-router";
import { NotFoundFallback } from "@shared/ui";
import "./unified-project-wrapper.scss";

const UnifiedProjectWrapper = () => {
  const { projectId, isLegacyProject } = useCoordinator();
  const location = useLocation();

  const {
    data: project = null,
    isLoading,
    isError,
    error,
  } = useQuery(
    projectQueries.getProject({
      projectId,
      idType: isLegacyProject ? "externalId" : "projectId",
    }),
  );

  useUpdateAbility({
    permissions: project?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder("project"),
  });

  const {
    data: objects,
    isLoading: isObjectsLoading,
  } = useObjects({ projectId });

  const shouldRedirectToFirstObject = () => {
    const { pathname } = location;
    const projectBasePath = `/u/projects/${projectId}/`;
    
    if (!pathname.startsWith(projectBasePath)) {
      return false;
    }
    
    const pathAfterProject = pathname.replace(projectBasePath, '');
    
    const projectLevelPaths = ['', 'settings/', 'members/', 'objects/', 'requisites/', 'statistics/', 'history/', 'related/'];
    
    return projectLevelPaths.some(path => pathAfterProject === path || pathAfterProject === path.slice(0, -1));
  };

  const needsRedirect = shouldRedirectToFirstObject();

  if (isLoading || (needsRedirect && isObjectsLoading))
    return (
      <div className="unified-project-wrapper-loading">
        <Spin size="large" tip="Загрузка проекта">
          <div className="unified-project-wrapper-loading__spin-placeholder" />
        </Spin>
      </div>
    );

  if (isError) {
    return (
      <NotFoundFallback
        status={isAxiosError(error) ? error.response?.status : undefined}
        className="unified-project-wrapper-not-found"
      />
    );
  }

  if (isLegacyProject) {
    return (
      <Navigate
        to={routes.project.root({ projectId: project?.projectId })}
        replace
      />
    );
  }

  if (needsRedirect && !isObjectsLoading) {
    const firstObject = objects?.buildingObjects?.[0];

    if (firstObject) {
      return (
        <Navigate
          to={routes.objects.object(projectId, firstObject.buildingObjectId)}
          replace
        />
      );
    } 
      return (
        <NotFoundFallback
          status={404}
          className="unified-project-wrapper-not-found"
        />
      );
    
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

export default UnifiedProjectWrapper;

